import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../App';
import { portalFetch, getValidToken } from '../lib/supabase';
import { playRingtone } from '../utils/ringtone';
import KitchenTicket from '../components/KitchenTicket';

function fmtDate(iso) {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtMoney(n) { return `$${(n || 0).toFixed(2)}`; }

function Card({ children, style }) {
  return <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, ...style }}>{children}</div>;
}

const ORDER_STATUS = {
  new:         { label: 'New',         bg: '#FEF3C7', color: '#92400E', next: 'in_progress', nextLabel: 'Start →' },
  in_progress: { label: 'In Progress', bg: '#DBEAFE', color: '#1E40AF', next: 'done',        nextLabel: 'Done ✓'  },
  done:        { label: 'Done',        bg: '#D1FAE5', color: '#065F46', next: null,           nextLabel: null      },
};

const GRID = '1fr 100px 90px 130px 100px 28px 36px';

export default function Orders() {
  const { restaurant } = useAuth();
  const [orders, setOrders]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState('today');
  const [expanded, setExpanded]       = useState(null);
  const [printTicket, setPrintTicket] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('other');
  const [cancelling, setCancelling]   = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(() =>
    localStorage.getItem('tc_sound_alerts') === 'true'
  );
  const prevCountRef = useRef(null);

  // ── LOAD ──────────────────────────────────────────────────────────────────
  const load = useCallback(async (silent = false) => {
    if (!restaurant?.id) return;
    if (!silent) setLoading(true);
    const data = await portalFetch(`/orders?filter=${filter}`);
    if (data?.orders) {
      const newOrders = data.orders;
      if (silent && soundEnabled && prevCountRef.current !== null) {
        const activeNow  = newOrders.filter(o => o.kitchen_status !== 'done').length;
        const activePrev = prevCountRef.current;
        if (activeNow > activePrev) {
          const diff = activeNow - activePrev;
          playRingtone();
          if (document.hidden && Notification.permission === 'granted') {
            new Notification(`${diff} new order${diff > 1 ? 's' : ''} at ${restaurant.name}`, {
              body: 'Check the TableChat portal',
              icon: '/favicon.ico',
            });
          }
        }
        prevCountRef.current = activeNow;
      } else if (prevCountRef.current === null) {
        prevCountRef.current = newOrders.filter(o => o.kitchen_status !== 'done').length;
      }
      setOrders(newOrders);
    }
    if (!silent) setLoading(false);
  }, [restaurant?.id, filter, soundEnabled]);

  useEffect(() => {
    prevCountRef.current = null;
    load();
  }, [load]);

  useEffect(() => {
    const interval = setInterval(() => load(true), 30000);
    return () => clearInterval(interval);
  }, [load]);

  // ── STATUS UPDATE — fixed: removed erroneous }() ──────────────────────────
  const updateStatus = async (orderId, newStatus) => {
    setOrders(prev => prev.map(o =>
      o.id === orderId ? { ...o, kitchen_status: newStatus } : o
    ));
    await portalFetch('/ticket-status', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'order', id: orderId, status: newStatus }),
    });
  };

  // ── CANCEL ORDER — fixed: moved out of useEffect so it's in scope ─────────
  const cancelOrder = async () => {
    if (!cancelModal) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const token = await getValidToken();
      const res = await fetch('/api/portal/cancel-order', {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ orderId: cancelModal, reason: cancelReason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Cancellation failed');
      setOrders(prev => prev.map(o =>
        o.id === cancelModal ? { ...o, status: 'cancelled', payment_status: 'refunded' } : o
      ));
      setCancelModal(null);
      setCancelReason('other');
    } catch (err) {
      setCancelError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  // ── SOUND TOGGLE ──────────────────────────────────────────────────────────
  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('tc_sound_alerts', String(next));
    if (next) {
      playRingtone();
      if (Notification.permission === 'default') Notification.requestPermission();
    }
  };

  const sorted = [...orders].sort((a, b) => {
    const aDone = a.kitchen_status === 'done' ? 1 : 0;
    const bDone = b.kitchen_status === 'done' ? 1 : 0;
    if (aDone !== bDone) return aDone - bDone;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const activeCount  = orders.filter(o => o.kitchen_status !== 'done').length;
  const todayRevenue = orders.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900, margin: 0 }}>Orders</h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 2 }}>{restaurant?.name}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={toggleSound}
            style={{
              padding: '0.5rem 1rem', borderRadius: 8, fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
              background: soundEnabled ? '#FEF3C7' : 'white',
              color: soundEnabled ? '#92400E' : 'var(--muted)',
              border: soundEnabled ? '1px solid #F59E0B' : '1px solid var(--border)',
            }}
          >
            {soundEnabled ? '🔔 Alerts On' : '🔕 Alerts Off'}
          </button>
          <button onClick={() => load()} style={{ background: 'var(--warm)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.5rem 1.1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', color: 'var(--ink)' }}>
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total',   value: orders.length },
          { label: 'Active',  value: activeCount,   color: activeCount > 0 ? 'var(--orange)' : undefined },
          { label: 'Done',    value: orders.length - activeCount },
          { label: 'Revenue', value: fmtMoney(todayRevenue) },
        ].map(({ label, value, color }) => (
          <Card key={label} style={{ padding: '1.2rem 1.4rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>{label}</div>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.7rem', fontWeight: 900, color: color || 'var(--orange)' }}>{value}</div>
          </Card>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[['today', 'Today'], ['week', 'This Week'], ['all', 'All']].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)} style={{
            padding: '0.5rem 1.1rem', borderRadius: 8, fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
            background: filter === val ? 'var(--orange)' : 'white',
            color: filter === val ? 'white' : 'var(--mid)',
            border: filter === val ? '1px solid var(--orange)' : '1px solid var(--border)',
          }}>{label}</button>
        ))}
      </div>

      <Card>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>Loading orders...</div>
        ) : orders.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.4 }}>🛍️</div>
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No orders for this period.</p>
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.75rem', padding: '0.75rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--warm)', borderRadius: '12px 12px 0 0' }}>
              {['Customer', 'Time', 'Total', 'Status', 'Action', '', '🖨'].map(h => (
                <div key={h} style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
              ))}
            </div>

            {sorted.map(order => {
              const isDone    = order.kitchen_status === 'done';
              const statusCfg = ORDER_STATUS[order.kitchen_status] || ORDER_STATUS.new;

              return (
                <div key={order.id}>
                  <div
                    style={{
                      display: 'grid', gridTemplateColumns: GRID, gap: '0.75rem',
                      padding: '0.9rem 1.4rem', borderBottom: '1px solid var(--warm)',
                      alignItems: 'center', fontSize: '0.875rem', cursor: 'pointer',
                      background: isDone ? '#FAFAFA' : 'white',
                      opacity: isDone ? 0.65 : 1,
                    }}
                    onClick={() => setExpanded(expanded === order.id ? null : order.id)}
                    onMouseEnter={e => { if (!isDone) e.currentTarget.style.background = 'var(--cream)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isDone ? '#FAFAFA' : 'white'; }}
                  >
                    {/* Customer */}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: isDone ? 'var(--muted)' : 'var(--dark)' }}>
                        {order.customer_name || 'Customer'}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 1 }}>
                        {order.fulfillment_method || 'pickup'}
                      </div>
                    </div>

                    {/* Time */}
                    <div style={{ fontSize: '0.82rem', color: 'var(--mid)' }}>{fmtDate(order.created_at)}</div>

                    {/* Total */}
                    <div style={{ fontWeight: 700, color: isDone ? 'var(--muted)' : 'var(--orange)' }}>{fmtMoney(order.total)}</div>

                    {/* Status badge */}
                    <div>
                      <span style={{
                        fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 10,
                        background: statusCfg.bg, color: statusCfg.color,
                      }}>{statusCfg.label}</span>
                    </div>

                    {/* Action button */}
                    <div onClick={e => e.stopPropagation()}>
                      {statusCfg.next && (
                        <button
                          onClick={() => updateStatus(order.id, statusCfg.next)}
                          style={{
                            fontSize: '0.72rem', fontWeight: 700, padding: '0.25rem 0.55rem',
                            borderRadius: 8, cursor: 'pointer', border: 'none',
                            background: statusCfg.next === 'done' ? '#D1FAE5' : '#DBEAFE',
                            color: statusCfg.next === 'done' ? '#065F46' : '#1E40AF',
                          }}
                        >
                          {statusCfg.nextLabel}
                        </button>
                      )}
                    </div>

                    {/* Expand toggle */}
                    <div style={{ color: 'var(--muted)', fontSize: '0.85rem', textAlign: 'center' }}>
                      {expanded === order.id ? '▲' : '▾'}
                    </div>

                    {/* Cancel button — only for recent paid orders */}
                    {order.payment_status === 'paid' && order.status !== 'cancelled' && (() => {
                      const ageMin = (Date.now() - new Date(order.created_at).getTime()) / 60000;
                      return ageMin <= 5;
                    })() && (
                      <div style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <button
                          title="Cancel order & issue refund"
                          onClick={() => { setCancelModal(order.id); setCancelError(null); }}
                          style={{ background: 'none', border: '1px solid #FCA5A5', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: '0.8rem', color: '#DC2626' }}
                        >✕</button>
                      </div>
                    )}

                    {/* Print button */}
                    <div style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <button
                        title="Print kitchen ticket"
                        onClick={() => setPrintTicket({
                          type: 'order',
                          ticketNumber: `TC-ORD-${order.id?.slice(-6).toUpperCase()}`,
                          customerName: order.customer_name || 'Customer',
                          customerPhone: order.customer_phone || '',
                          timestamp: new Date(order.created_at).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
                          items: (order.order_items || []).map(item => ({
                            name: item.dish_name || item.name || '',
                            qty: item.quantity || item.qty || 1,
                            notes: item.notes || '',
                          })),
                          pickupTime: order.pickup_time || '',
                          fulfillmentMethod: order.fulfillment_method || 'pickup',
                          deliveryAddress: order.delivery_address || null,
                        })}
                        style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 6px', cursor: 'pointer', fontSize: '0.9rem' }}
                      >🖨</button>
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {expanded === order.id && (
                    <div style={{ background: 'var(--cream)', borderBottom: '1px solid var(--border)', padding: '1rem 1.4rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                        <div>
                          <p style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>Items</p>
                          {(order.order_items || []).map((item, i) => (
                            <div key={i} style={{ marginBottom: 6 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                                <span>{item.quantity || 1}x {item.dish_name}</span>
                                <span style={{ color: 'var(--muted)' }}>{fmtMoney((item.dish_price || 0) * (item.quantity || 1))}</span>
                              </div>
                              
                              {(item.modifiers || []).map((mod, mi) => (
                                <div key={mi} style={{ fontSize: '0.78rem', color: 'var(--muted)', paddingLeft: '1rem' }}>
                                  + {mod.modifier_name}{mod.price_delta > 0 ? ` (+$${(mod.price_delta/100).toFixed(2)})` : ''}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                        <div>
                          <p style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>Details</p>
                          <div style={{ fontSize: '0.875rem', color: 'var(--mid)', lineHeight: 1.8 }}>
                            <div>📧 {order.customer_email || '–'}</div>
                            <div>📞 {order.customer_phone || '–'}</div>
                            <div>🕐 Pickup: {order.pickup_time || '–'}</div>
                            <div>💳 {order.payment_status}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {printTicket && (
        <KitchenTicket ticket={printTicket} onClose={() => setPrintTicket(null)} />
      )}

      {/* Cancel Order Modal */}
      {cancelModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: '1.5rem', maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700 }}>Cancel Order</h3>
            <p style={{ margin: '0 0 1.2rem', fontSize: '0.875rem', color: '#555' }}>
              This will issue a full refund and notify the customer by email. This cannot be undone.
            </p>
            <div style={{ marginBottom: '1.2rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                Reason for cancellation
              </label>
              <select
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1.5px solid #E0E0E0', borderRadius: 8, fontSize: '0.9rem', fontFamily: 'inherit', background: 'white' }}
              >
                <option value="out_of_ingredients">Out of ingredients for this order</option>
                <option value="too_busy">Kitchen at full capacity right now</option>
                <option value="closing_early">Restaurant closing early today</option>
                <option value="other">Other / unexpected circumstances</option>
              </select>
            </div>
            {cancelError && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, fontSize: '0.875rem', color: '#DC2626' }}>
                {cancelError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => { setCancelModal(null); setCancelError(null); }}
                style={{ flex: 1, padding: '0.7rem', border: '1px solid #E0E0E0', borderRadius: 8, background: 'white', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', color: '#555' }}
              >Keep Order</button>
              <button
                onClick={cancelOrder}
                disabled={cancelling}
                style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: 8, background: '#DC2626', color: 'white', fontSize: '0.9rem', fontWeight: 700, cursor: cancelling ? 'not-allowed' : 'pointer', opacity: cancelling ? 0.7 : 1 }}
              >{cancelling ? 'Cancelling...' : 'Cancel & Refund'}</button>
            </div>
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.75rem', color: '#9CA3AF', textAlign: 'center' }}>
              Refunds typically appear within 5–10 business days
            </p>
          </div>
        </div>
      )}
    </>
  );
}
